import { useEffect, useState } from 'react';
import {
  Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, Tooltip, Typography,
} from '@mui/material';
import { DragIndicator } from '@mui/icons-material';
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { mergeColumnOrder } from '../../utils/merge-column-order';

export interface ColumnChoice {
  field: string;
  label: string;
  /** False when this grouping does not produce the column. It is still listed and orderable. */
  available: boolean;
}

function SortableRow({ item, checked, onToggle }: {
  item: ColumnChoice; checked: boolean; onToggle: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.field });
  return (
    <Box
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 }}
      sx={{ display: 'flex', alignItems: 'center', gap: 0.5, py: 0.25, borderRadius: 1,
        '&:hover': { bgcolor: 'action.hover' } }}
    >
      <IconButton size="small" sx={{ cursor: 'grab', p: 0.3, touchAction: 'none' }} {...attributes} {...listeners}>
        <DragIndicator sx={{ fontSize: 18, color: 'text.secondary' }} />
      </IconButton>
      <Checkbox size="small" checked={checked} onChange={onToggle} />
      <Typography variant="body2" sx={{ flex: 1, color: item.available ? 'text.primary' : 'text.disabled' }}>
        {item.label}
      </Typography>
      {!item.available && (
        <Tooltip title="Not produced by the current grouping. Your choice is kept for when it is.">
          <Typography variant="caption" color="text.disabled">not in this view</Typography>
        </Tooltip>
      )}
    </Box>
  );
}

/**
 * The whole catalogue, always — a column this grouping does not produce is still listed,
 * so the stored value is exactly what this list shows, with nothing to reconcile.
 */
export default function ColumnsDialog({ open, onClose, catalogue, order, hidden, onSave }: {
  open: boolean;
  onClose: () => void;
  catalogue: ColumnChoice[];
  order: string[];
  hidden: string[];
  onSave: (order: string[], hidden: string[]) => void;
}) {
  const [items, setItems] = useState<string[]>([]);
  const [off, setOff] = useState<Set<string>>(new Set());

  // Seeded on open only: re-seeding while open would discard a drag.
  useEffect(() => {
    if (!open) return;
    setItems(mergeColumnOrder(order, catalogue.map((c) => c.field)));
    setOff(new Set(hidden));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const byField = new Map(catalogue.map((c) => [c.field, c]));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 0.5 }}>Columns</DialogTitle>
      <DialogContent dividers sx={{ maxHeight: 460 }}>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={(e: DragEndEvent) => {
            const { active, over } = e;
            if (!over || active.id === over.id) return;
            setItems((prev) => arrayMove(prev, prev.indexOf(String(active.id)), prev.indexOf(String(over.id))));
          }}
        >
          <SortableContext items={items} strategy={verticalListSortingStrategy}>
            {items.map((f) => {
              const item = byField.get(f);
              if (!item) return null;
              return (
                <SortableRow
                  key={f}
                  item={item}
                  checked={!off.has(f)}
                  onToggle={() => setOff((prev) => {
                    const next = new Set(prev);
                    if (next.has(f)) next.delete(f); else next.add(f);
                    return next;
                  })}
                />
              );
            })}
          </SortableContext>
        </DndContext>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => { setItems(catalogue.map((c) => c.field)); setOff(new Set()); }}>Reset</Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => { onSave(items, [...off]); onClose(); }}>Apply</Button>
      </DialogActions>
    </Dialog>
  );
}
